/** ステップ2: 契約者情報の登録 (会社+社長 / req1)。ログイン発行は別途運営 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsYouType } from 'libphonenumber-js';
import { purchaseApi } from '../api/purchaseApi';
import { loadState, saveState } from '../state/purchaseState';
import { PurchaseLayout } from './PurchaseLayout';

const FIELDS = [
  { key: 'name', label: '会社名', ph: '株式会社サンプル' },
  { key: 'zip', label: '郵便番号', ph: '100-0001', hint: '7桁を入力すると住所（都道府県〜）を自動補完します' },
  { key: 'address', label: '住所', ph: '東京都千代田区千代田1-1', hint: '都道府県名から入力してください。自動補完後は番地・建物名を追記してください' },
  { key: 'tel', label: '電話番号', ph: '03-1234-5678' },
  { key: 'presidentName', label: '代表者氏名', ph: '山田 太郎' },
  { key: 'email', label: 'メールアドレス（ログインID）', ph: 'shacho@example.co.jp', type: 'email', hint: 'このメールアドレスがログインIDになります' },
  { key: 'emailConfirm', label: 'メールアドレス（確認）', ph: '同じメールアドレスを再入力', type: 'email', confirm: true },
  { key: 'settlementEmail', label: '請求書送付先メールアドレス（任意）', ph: 'settlement@example.co.jp', type: 'email', optional: true, hint: '請求書メールのCCに追加されます。（未入力可）' },
];

/**
 * 郵便番号(7桁)から住所(都道府県+市区町村+町域)を取得する。失敗時はnull(手入力にフォールバック)。
 * ブラウザ直呼びは zipcloud が CORS 非対応のため不可 → バックエンド(api-handler)経由でプロキシする。
 */
async function lookupAddressByZip(zip7: string): Promise<string | null> {
  try {
    const { address } = await purchaseApi.lookupZipAddress(zip7);
    return address ?? null;
  } catch {
    /* ネットワーク失敗時は手入力にフォールバック */
    return null;
  }
}

/** 掛け払い(MFK)は住所が都道府県名で始まることを要求する */
const PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県', '茨城県', '栃木県', '群馬県',
  '埼玉県', '千葉県', '東京都', '神奈川県', '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県', '徳島県', '香川県', '愛媛県', '高知県', '福岡県',
  '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

export const PurchaseAccountPage: React.FC = () => {
  const nav = useNavigate();
  // 戻る操作で復元できるよう、保存済みの入力内容から初期化する
  const [form, setForm] = useState<Record<string, string>>(() => loadState().account || {});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 既に登録済みメールの場合にログイン誘導を表示する
  const [existingEmail, setExistingEmail] = useState<string | null>(null);

  useEffect(() => {
    if (Object.keys(loadState().cart).filter((k) => loadState().cart[k].checked).length === 0) {
      nav('/purchase');
    }
  }, [nav]);

  // 入力内容を localStorage に保持し、戻る操作でも消えないようにする
  useEffect(() => {
    const st = loadState();
    saveState({ ...st, account: form });
  }, [form]);

  // 郵便番号: 1文字ごとに数字のみへ整形＋ハイフン自動挿入。7桁到達で住所を自動補完する。
  const onZipChange = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '').slice(0, 7);
    const formatted = digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
    setForm((prev) => ({ ...prev, zip: formatted }));
    if (digits.length === 7) {
      void lookupAddressByZip(digits).then((addr) => {
        if (addr) setForm((prev) => ({ ...prev, address: addr }));
      });
    }
  };

  // 電話番号: 数字入力で日本の書式へ自動整形（携帯/固定/フリーダイヤル対応）。
  // AsYouType はハイフンを自動挿入する。バックエンドは送信時に digits() で除去するため保存形式は不問。
  const onTelChange = (value: string) => {
    const formatted = new AsYouType('JP').input(value);
    setForm((prev) => ({ ...prev, tel: formatted }));
  };

  // 郵便番号の数字桁数（0〜7）。7未満のときに注意表示を出す。
  const zipDigits = (form.zip || '').replace(/[^0-9]/g, '');

  const submit = async () => {
    setErr(null);
    setExistingEmail(null);
    const missing = FIELDS.filter((f) => !f.optional && !(form[f.key] || '').trim());
    if (missing.length) return setErr(`未入力: ${missing.map((m) => m.label).join('、')}`);
    // メールアドレス2回入力の一致確認
    const email = (form.email || '').trim();
    if (email !== (form.emailConfirm || '').trim()) {
      return setErr('確認用のメールアドレスが一致しません。');
    }
    // 掛け払い要件: 住所は都道府県名で始まること（未充足だと決済時にMFKで弾かれるため事前に案内）
    const addr = (form.address || '').trim();
    if (!PREFECTURES.some((p) => addr.startsWith(p))) {
      return setErr('住所は都道府県名から入力してください（例: 東京都千代田区丸の内1-1-1）。');
    }
    setBusy(true);
    try {
      // 決済に進む前にメール存在チェック（初回のみ）。登録済みならログインへ誘導。
      const chk = await purchaseApi.checkEmail(email);
      if (chk.exists) {
        setExistingEmail(email);
        setBusy(false);
        return;
      }
      // 未登録 → アカウント発行（招待メール=仮PW）込みで会社+社長を作成
      const { emailConfirm: _omit, ...payload } = form;
      void _omit;
      const res = await purchaseApi.createCompany(payload as never);
      const st = loadState();
      saveState({ ...st, billingCompanyId: res.billingCompany.id });
      nav('/purchase/confirm');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '登録に失敗しました';
      // TOCTOU 保険: 送信直前に登録された等で 409 になった場合もログイン誘導
      if (/既に登録/.test(msg)) {
        setExistingEmail(email);
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <PurchaseLayout step={2} sectionTitle="契約情報の入力" sectionSub="契約者情報をご入力ください。請求書の宛先となる会社情報と代表者情報を入力します。">
      <div className="pf-form">
        {err && <div className="pf-err">{err}</div>}
        {existingEmail && (
          <div className="pf-err" role="alert" style={{ background: '#eef4ff', color: '#1a3a6b', border: '1px solid #c7d8f5' }}>
            <div style={{ marginBottom: 8 }}>
              このメールアドレス（{existingEmail}）は既に登録されています。ログインしてお手続きください。
            </div>
            <button className="go" onClick={() => nav('/login')}>ログインへ</button>
          </div>
        )}
        {FIELDS.map((f) => (
          <div className="pf-field" key={f.key}>
            <label>{f.label}</label>
            <input
              type={f.type || 'text'}
              placeholder={f.ph}
              value={form[f.key] || ''}
              inputMode={f.key === 'zip' || f.key === 'tel' ? 'numeric' : undefined}
              maxLength={f.key === 'zip' ? 8 : undefined}
              onChange={(e) =>
                f.key === 'zip'
                  ? onZipChange(e.target.value)
                  : f.key === 'tel'
                    ? onTelChange(e.target.value)
                    : setForm({ ...form, [f.key]: e.target.value })
              }
            />
            {f.hint && <small style={{ color: '#6b7684', fontSize: 12 }}>{f.hint}</small>}
            {f.key === 'zip' && zipDigits.length > 0 && zipDigits.length < 7 && (
              <small style={{ color: '#c0392b', fontSize: 12 }}>
                郵便番号は7桁の数字で入力してください（例: 100-0001）
              </small>
            )}
          </div>
        ))}
        <div className="pf-actions">
          <button className="back" onClick={() => nav('/purchase')}>戻る</button>
          <button className="go" disabled={busy} onClick={submit}>{busy ? '確認中…' : 'お申し込み内容の確認に進む'}</button>
        </div>
      </div>
    </PurchaseLayout>
  );
};

export default PurchaseAccountPage;
